/**
 * "Your daily credits come back at {time}." — the line under the daily OUT title (credits widgets,
 * personal-sale banner, settings daily bar). The instant is the BFF's `daily_limit.resets_at`
 * (`DailyLimit.resetsAtIso`); this file only decides whether to say it and how to write the time.
 *
 * Formatting rule, identical on web, iOS and Android: the viewer's LOCAL time in the app locale's short
 * time style (`timeStyle: 'short'`); when the instant is not on the viewer's today, the short weekday
 * leads ("Tue 15:11"). Pure — the clock, locale and zone are arguments — so it is unit-tested.
 */

export interface DailyResetFormatOptions {
  /** The clock (epoch ms). */
  now: number;
  /** The app's active locale (i18n language), or undefined for the runtime default. */
  locale?: string;
  /** IANA zone to read "today" and the time in; undefined = the viewer's own zone. Tests pin it. */
  timeZone?: string;
}

/** Calendar day key (y-m-d) of an instant in a zone — locale-independent so the comparison is exact. */
function dayKey(epochMs: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(epochMs);
}

/**
 * The `{time}` value for the reset line, or null when there is nothing honest to say: no instant, an
 * unparseable one, or one already behind us (the refetch timer is about to replace the state).
 */
export function formatDailyCreditsResetTime(resetsAtIso: string | null | undefined, options: DailyResetFormatOptions): string | null {
  if (!resetsAtIso) return null;
  const at = new Date(resetsAtIso).getTime();
  if (!Number.isFinite(at) || at <= options.now) return null;
  const { locale, timeZone } = options;
  let time: string;
  try {
    time = new Intl.DateTimeFormat(locale, { timeStyle: 'short', timeZone }).format(at);
  } catch {
    // An i18n language code the runtime cannot resolve — fall back to its default locale.
    time = new Intl.DateTimeFormat(undefined, { timeStyle: 'short', timeZone }).format(at);
  }
  if (dayKey(at, timeZone) === dayKey(options.now, timeZone)) return time;
  let weekday: string;
  try {
    weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone }).format(at);
  } catch {
    weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone }).format(at);
  }
  return `${weekday} ${time}`;
}

/** The facts a surface has about its credit state — the widget state's shape, or the settings bar's. */
export interface DailyResetLineInputs {
  /** The daily allowance is spent (the widget's `level === 'out'` on the `daily` pool, or `remaining <= 0`). */
  dailyLimitReached: boolean;
  resetsAtIso: string | null | undefined;
}

/**
 * The `{time}` for a surface, or null → render no line. Only when the daily limit is reached AND the
 * reset instant is known and still ahead.
 */
export function dailyCreditsResetTime(inputs: DailyResetLineInputs, options: DailyResetFormatOptions): string | null {
  if (!inputs.dailyLimitReached) return null;
  return formatDailyCreditsResetTime(inputs.resetsAtIso, options);
}

/** The widget state → the reset-line inputs: only the DAILY pool's OUT state says when it comes back.
 *  Structural (no import of the widget-state module) so this file stays dependency-free for jest. */
export function widgetStateResetInputs(state: { level: string; pool: string; dailyResetsAtIso?: string | null }): DailyResetLineInputs {
  return { dailyLimitReached: state.level === 'out' && state.pool === 'daily', resetsAtIso: state.dailyResetsAtIso };
}
